"use client";

import Image from "next/image";
import { motion } from "motion/react";
import { Calendar, MapPin, DollarSign, CheckCircle } from "lucide-react";
import { useTranslations } from "next-intl";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/i18n/navigation";

export default function MissionShowcaseSection() {
  const t = useTranslations("MissionShowcase");

  return (
    <div className="bg-white py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-16 text-center">
          <h2 className="mb-4 text-4xl font-bold text-gray-900">
            {t("title")}
          </h2>
          <p className="mx-auto max-w-2xl landing-text-lead text-gray-600">
            {t("subtitle")}
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mx-auto max-w-5xl"
        >
          <Card className="min-w-0 overflow-hidden">
            <div className="grid min-w-0 gap-0 lg:grid-cols-2">
              <div className="min-w-0 bg-gray-50 p-4 min-[360px]:p-8">
                <div className="mb-6 flex min-w-0 flex-col items-start gap-3 min-[360px]:flex-row min-[360px]:items-center min-[360px]:justify-between">
                  <h3 className="min-w-0 break-words text-2xl font-bold text-gray-900">
                    {t("sampleTitle")}
                  </h3>
                  <Badge className="landing-text-detail shrink-0 bg-green-100 text-green-800 hover:bg-green-100">
                    {t("badgeInProgress")}
                  </Badge>
                </div>

                <div className="mb-8 space-y-4">
                  <div className="flex min-w-0 items-start">
                    <MapPin className="mt-1 mr-3 h-5 w-5 shrink-0 text-gray-400" />
                    <div className="min-w-0">
                      <div className="landing-text-body font-medium text-gray-900">
                        {t("locationLabel")}
                      </div>
                      <div className="break-words landing-text-body text-gray-600">
                        {t("locationValue")}
                      </div>
                    </div>
                  </div>
                  <div className="flex min-w-0 items-start">
                    <Calendar className="mt-1 mr-3 h-5 w-5 shrink-0 text-gray-400" />
                    <div className="min-w-0">
                      <div className="landing-text-body font-medium text-gray-900">
                        {t("timelineLabel")}
                      </div>
                      <div className="break-words landing-text-body text-gray-600">
                        {t("timelineValue")}
                      </div>
                    </div>
                  </div>
                  <div className="flex min-w-0 items-start">
                    <DollarSign className="mt-1 mr-3 h-5 w-5 shrink-0 text-gray-400" />
                    <div className="min-w-0">
                      <div className="landing-text-body font-medium text-gray-900">
                        {t("budgetLabel")}
                      </div>
                      <div className="break-words landing-text-body text-gray-600">
                        {t("budgetValue")}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h4 className="mb-4 font-semibold text-gray-900">
                    {t("milestonesTitle")}
                  </h4>
                  <div className="space-y-3">
                    <div className="grid min-w-0 grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-x-3 gap-y-1 min-[360px]:grid-cols-[1.25rem_minmax(0,1fr)_auto]">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <div className="min-w-0">
                        <span className="landing-text-body font-medium text-gray-900">
                          {t("milestone1")}
                        </span>
                      </div>
                      <span className="col-start-2 landing-text-detail text-green-600 min-[360px]:col-start-3 min-[360px]:row-start-1">
                        {t("milestone1Status")}
                      </span>
                    </div>
                    <div className="grid min-w-0 grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-x-3 gap-y-1 min-[360px]:grid-cols-[1.25rem_minmax(0,1fr)_auto]">
                      <div className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-accent">
                        <div className="h-2 w-2 rounded-full bg-accent" />
                      </div>
                      <div className="min-w-0">
                        <span className="landing-text-body font-medium text-gray-900">
                          {t("milestone2")}
                        </span>
                      </div>
                      <span className="col-start-2 landing-text-detail text-accent min-[360px]:col-start-3 min-[360px]:row-start-1">
                        {t("milestone2Status")}
                      </span>
                    </div>
                    <div className="grid min-w-0 grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-x-3 gap-y-1 min-[360px]:grid-cols-[1.25rem_minmax(0,1fr)_auto]">
                      <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
                      <div className="min-w-0">
                        <span className="landing-text-body font-medium text-gray-500">
                          {t("milestone3")}
                        </span>
                      </div>
                      <span className="col-start-2 landing-text-detail text-gray-500 min-[360px]:col-start-3 min-[360px]:row-start-1">
                        {t("milestone3Status")}
                      </span>
                    </div>
                  </div>
                </div>

                <details className="mt-8 rounded-lg border border-slate-200 p-4">
                  <summary className="cursor-pointer font-semibold text-primary">{t("btnViewDetails")}</summary>
                  <p className="mt-3 landing-text-detail">{t("demoNotice")}</p>
                </details>
              </div>

              <div className="relative flex min-h-[500px] min-w-0 items-start justify-center bg-gray-200">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20">
                  <Image
                    src={t("missionImageSrc")}
                    alt={t("mapAlt")}
                    fill
                    sizes="(min-width: 1024px) 50vw, 100vw"
                    className="object-cover object-[50%_80%]"
                  />
                </div>
                <div className="relative z-10 mx-3 mt-8 max-w-[calc(100%-1.5rem)] min-w-0 rounded-lg bg-white/80 p-4 text-center backdrop-blur-sm min-[360px]:mx-4 min-[360px]:max-w-md min-[360px]:p-8">
                  <div className="mb-4 inline-flex h-20 w-20 items-center justify-center rounded-full bg-white shadow-lg">
                    <MapPin className="h-10 w-10 text-accent" />
                  </div>
                  <h4 className="mb-2 text-xl font-semibold text-gray-900">
                    {t("mapOverlayTitle")}
                  </h4>
                  <p className="landing-text-body text-gray-600">
                    {t("mapOverlayBody")}
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        <div className="mt-12 text-center">
          <Button
            size="lg"
            variant="outline"
            className="landing-text-action max-w-full whitespace-normal border-primary px-4 text-center text-primary hover:bg-primary hover:text-white min-[360px]:px-8"
            asChild
          >
            <Link href={t("ctaExploreHref")}>{t("ctaExplore")}</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
